import React, { useState } from 'react';
import { Box, TextField, Stack, Typography, Button, useMediaQuery, Grid, Autocomplete } from '@mui/material';
import * as yup from 'yup';
import { useFormik } from 'formik';
import { useDispatch } from 'react-redux';
import { createMarketTrendsAnalysis } from '../../services/marketTrendsService';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';

import { openSnackbar } from '../../store/reducers/snackbar';
import { setMarketTrends, setLoadingAnswer } from '../../store/reducers/actions';
import { isValidWebsiteUrl, normalizeWebsiteUrl } from 'utils/urlValidation';

const industries = [
  '3D Printing & Additive Manufacturing',
  '5G & Telecommunications Infrastructure',
  'Aerospace & Defense',
  'Agriculture & Farming',
  'Artificial Intelligence & ML',
  'Augmented Reality (AR)',
  'Automotive & Transportation',
  'Banking & Financial Services',
  'Biodiversity & Conservation',
  'Biotechnology',
  'Business Process Outsourcing',
  'Carbon Capture & Storage',
  'Chemical Manufacturing',
  'Circular Economy',
  'Clean Energy & Renewables',
  'Cloud Computing & Services',
  'Construction & Real Estate',
  'Consulting Services',
  'Consumer Electronics',
  'Consumer Goods & Retail',
  'Content Creation & Media',
  'Cosmetics & Personal Care',
  'Coworking & Office Services',
  'Customer Experience (CX)',
  'Cybersecurity',
  'Data Analytics & Insights',
  'DevOps & Site Reliability',
  'Digital Health & Telemedicine',
  'Digital Marketing & Advertising',
  'Digital Payment Solutions',
  'E-commerce & Online Retail',
  'Education & E-learning',
  'Entertainment & Media',
  'Environmental Services',
  'Fashion & Apparel',
  'Food & Beverage',
  'Furniture & Home Goods',
  'Gaming & Esports',
  'Green Technology',
  'Healthcare & Medical Devices',
  'Hospitality & Tourism',
  'Human Resources & Recruitment',
  'Industrial Manufacturing',
  'Information Technology',
  'Insurance',
  'Internet of Things (IoT)',
  'Last-mile Delivery',
  'Legal Services',
  'Logistics & Supply Chain',
  'Luxury Goods',
  'Machine Learning & AI',
  'Maritime & Shipping',
  'Marketing Automation',
  'Medical Research & Development',
  'Mental Health & Wellness',
  'Microservices & API Development',
  'Mining & Metals',
  'Mobile Apps & Development',
  'Nanotechnology',
  'Natural Language Processing',
  'Pet Care & Services',
  'Pharmaceuticals',
  'Plant-based & Alternative Foods',
  'Professional Services',
  'Public Relations',
  'Publishing & Digital Media',
  'Quantum Computing',
  'Renewable Energy',
  'Retail & Consumer Goods',
  'Robotics & Automation',
  'Semiconductor & Microchip Production',
  'Sharing Economy Services',
  'Smart Cities & Infrastructure',
  'Smart Manufacturing',
  'Social Media & Networking',
  'Software Development',
  'Space Technology & Exploration',
  'Sports & Fitness',
  'Subscription Services',
  'Sustainable Fashion',
  'Telecommunications',
  'Textiles & Fabrics',
  'Transportation & Logistics',
  'Travel & Tourism',
  'Utilities & Power',
  'Venture Capital & Private Equity',
  'Virtual Reality (VR)',
  'Waste Management',
  'Water Treatment & Conservation',
  'Web3 & Blockchain'
];

const MarketTrendsInput = () => {
  const dispatch = useDispatch();
  const matchDownSM = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const [loading, setLoading] = useState(false);
  const [selectedIndustry, setSelectedIndustry] = useState(null);

  const validationSchema = yup.object().shape({
    input: yup
      .string()
      .test('is-url-or-valid-industry', function (value) {
        if (!value) return false;
        if (isValidWebsiteUrl(value)) return true;
        // For industry input, check if it contains valid characters
        const sanitizedRegex = /^[a-zA-Z0-9\s&\-(),.]+$/;
        if (!sanitizedRegex.test(value)) {
          return this.createError({
            message: 'Please enter a valid URL or an industry name without special characters'
          });
        }
        return true;
      })
      .test('length-check', 'Input cannot exceed 2048 characters', function (value) {
        if (!value) return true;
        if (isValidWebsiteUrl(value)) return value.length <= 2048;
        return value.length <= 150;
      })
      .required('Please enter a valid URL or industry name')
  });

  const handleSubmit = async (values) => {
    setLoading(true);
    dispatch(setLoadingAnswer(true));
    try {
      const isUrl = isValidWebsiteUrl(values.input);
      const normalizedInput = isUrl ? normalizeWebsiteUrl(values.input) : values.input;

      // If it's not a URL, it's an industry name
      const response = await createMarketTrendsAnalysis(normalizedInput, isUrl ? 'url' : 'industry');

      if (response && response.success_status) {
        dispatch(setMarketTrends(response));
      } else if (response.error) {
        dispatch(
          openSnackbar({
            open: true,
            message: response.error || 'Failed to generate market trends. Please try again.',
            variant: 'alert',
            alert: {
              color: 'error'
            },
            close: false
          })
        );
      }
    } catch (error) {
      console.error('Error fetching market trends:', error.response?.data?.error || error.message);
      dispatch(
        openSnackbar({
          open: true,
          message: error.response?.data?.error || 'Failed to generate market trends. Please try again.',
          variant: 'alert',
          alert: {
            color: 'error'
          },
          close: false
        })
      );
    } finally {
      setLoading(false);
      dispatch(setLoadingAnswer(false));
    }
  };

  const formik = useFormik({
    initialValues: {
      input: ''
    },
    validationSchema,
    onSubmit: handleSubmit
  });

  return (
    <Stack direction="column" spacing={3} alignItems="flex-start">
      <form onSubmit={formik.handleSubmit}>
        <Grid container pb={2}>
          <Grid item xs={12}>
            <Stack pt={1} display="flex" flexDirection={matchDownSM ? 'column' : 'row'} alignItems="center" justifyContent="center">
              <Box m={1}>
                <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                  <TrendingUpIcon sx={{ fontSize: 32, color: 'primary.main' }} />
                  <Typography variant="h1" color="textSecondary" align="center">
                    Market Trends Analysis
                  </Typography>
                </Stack>
                <Typography variant="h6" pt={1} color="textSecondary" align="center">
                  Enter your website URL to analyze market trends
                </Typography>
              </Box>
            </Stack>
          </Grid>

          <Grid item xs={12}>
            <Stack
              display="flex"
              flexDirection={matchDownSM ? 'column' : 'row'}
              alignItems={matchDownSM ? 'center' : 'flex-start'}
              justifyContent="center"
              m={0}
              mb={formik.errors.input ? -2.9 : 0}
              pt={2}
              spacing={matchDownSM ? 2 : 0}
            >
              <Box
                sx={{
                  minWidth: matchDownSM ? '100%' : 276,
                  minHeight: matchDownSM ? 65 : 'auto'
                }}
              >
                <Autocomplete
                  value={selectedIndustry}
                  onChange={(event, newValue) => {
                    setSelectedIndustry(newValue);
                    formik.setFieldValue('input', newValue || '');
                  }}
                  options={industries}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      id="input"
                      name="input"
                      placeholder="Enter website URL or select industry"
                      error={Boolean(formik.errors.input)}
                      helperText={formik.errors.input}
                      sx={{
                        minWidth: matchDownSM ? '100%' : 276
                      }}
                      autoFocus
                      value={formik.values.input}
                      onChange={(e) => {
                        formik.handleChange(e);
                        setSelectedIndustry(e.target.value);
                      }}
                      onBlur={formik.handleBlur}
                      disabled={loading}
                    />
                  )}
                  freeSolo
                  autoSelect
                  disableClearable
                  ListboxProps={{
                    sx: { maxHeight: 'none' }
                  }}
                  filterOptions={(options, { inputValue }) => {
                    return options.filter((option) => option.toLowerCase().includes(inputValue.toLowerCase())).slice(0, 8);
                  }}
                />
              </Box>

              <Button
                variant="gradient"
                color="primary"
                sx={{
                  ml: matchDownSM ? 0 : 2,
                  mt: matchDownSM ? 0 : 0.3
                }}
                type="submit"
                disabled={loading}
              >
                {loading ? 'Analyzing...' : 'Analyze Trends'}
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </form>
    </Stack>
  );
};

export default MarketTrendsInput;
